import * as coreClient from '@azure/core-client';
import * as Parameters from './models/parameters';
import * as Mappers from './models/mappers';
import {
  HostOptions,
  TicketFaresAPIOptionalParams,
  GetFareSelectorListOptionalParams,
  GetFareSelectorListResponse,
  GetFareOptionalParams,
  GetFareResponse,
  GetFareByQueryStringTicketCodeOptionalParams,
  GetFareByQueryStringTicketCodeResponse,
  GetFareByBundleOptionalParams,
  GetFareByBundleResponse,
  GetFareByTicketIDOptionalParams,
  GetFareByTicketIDResponse,
} from './models';

export class TicketFaresAPI extends coreClient.ServiceClient {
  $host: HostOptions;

  /**
   * Initializes a new instance of the TicketFaresAPI class.
   * @param options The parameter options
   */
  constructor(options?: TicketFaresAPIOptionalParams) {
    // Initializing default values for options
    if (!options) {
      options = {};
    }
    const defaults: TicketFaresAPIOptionalParams = {
      requestContentType: 'application/json; charset=utf-8',
    };

    const packageDetails = `azsdk-js-ticketFaresAPI/1.0.0-beta.1`;
    const userAgentPrefix =
      options.userAgentOptions && options.userAgentOptions.userAgentPrefix
        ? `${options.userAgentOptions.userAgentPrefix} ${packageDetails}`
        : `${packageDetails}`;

    const optionsWithDefaults = {
      ...defaults,
      ...options,
      userAgentOptions: {
        userAgentPrefix,
      },
      baseUri:
        options.endpoint ??
        options.baseUri ??
        'https://nxbus.api.nationalexpress.com',
    };
    super(optionsWithDefaults);

    // Assigning values to Constant parameters
    this.$host = options.$host || 'https://nxbus.api.nationalexpress.com';
  }

  /**
   * Get Fare Selector List
   * @param options The options parameters.
   */
  getFareSelectorList(
    options?: GetFareSelectorListOptionalParams
  ): Promise<GetFareSelectorListResponse> {
    return this.sendOperationRequest(
      { options },
      getFareSelectorListOperationSpec
    );
  }

  /**
   * Get Fare (TicketProduct) Filtered By Fare Selectors
   * @param options The options parameters.
   */
  getFare(options?: GetFareOptionalParams): Promise<GetFareResponse> {
    return this.sendOperationRequest({ options }, getFareOperationSpec);
  }

  /**
   * Get Fare (TicketProduct) by Query String as Ticket Code - Deep Linking
   * @param ticketCode Ticket Code (either mTicketID or SwiftTicketID)
   * @param options The options parameters.
   */
  getFareByQueryStringTicketCode(
    ticketCode: string,
    options?: GetFareByQueryStringTicketCodeOptionalParams
  ): Promise<GetFareByQueryStringTicketCodeResponse> {
    return this.sendOperationRequest(
      { ticketCode, options },
      getFareByQueryStringTicketCodeOperationSpec
    );
  }

  /**
   * Get Fare (TicketProduct) Filtered By Bundle
   * @param options The options parameters.
   */
  getFareByBundle(
    options?: GetFareByBundleOptionalParams
  ): Promise<GetFareByBundleResponse> {
    return this.sendOperationRequest({ options }, getFareByBundleOperationSpec);
  }

  /**
   * Get Fare (TicketProduct) Filtered By Ticket ID
   * @param ticketId Ticket Id
   * @param options The options parameters.
   */
  getFareByTicketID(
    ticketId: string,
    options?: GetFareByTicketIDOptionalParams
  ): Promise<GetFareByTicketIDResponse> {
    return this.sendOperationRequest(
      { ticketId, options },
      getFareByTicketIDOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const getFareSelectorListOperationSpec: coreClient.OperationSpec = {
  path: '/fares/V1/fareSelectors',
  httpMethod: 'POST',
  responses: {
    200: {
      bodyMapper: Mappers.FareSelectorList,
    },
    default: {
      bodyMapper: Mappers.ApiError,
    },
  },
  requestBody: Parameters.body,
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: 'json',
  serializer,
};
const getFareOperationSpec: coreClient.OperationSpec = {
  path: '/fares/V1/fare',
  httpMethod: 'POST',
  responses: {
    200: {
      bodyMapper: {
        type: {
          name: 'Sequence',
          element: { type: { name: 'Composite', className: 'TicketProduct' } },
        },
      },
    },
    default: {
      bodyMapper: Mappers.ApiError,
    },
  },
  requestBody: Parameters.body1,
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: 'json',
  serializer,
};
const getFareByQueryStringTicketCodeOperationSpec: coreClient.OperationSpec = {
  path: '/fares/V1/fare',
  httpMethod: 'GET',
  responses: {
    200: {
      bodyMapper: Mappers.TicketProduct,
    },
    default: {
      bodyMapper: Mappers.ApiError,
    },
  },
  queryParameters: [Parameters.ticketCode],
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept],
  serializer,
};
const getFareByBundleOperationSpec: coreClient.OperationSpec = {
  path: '/fares/V1/fare/bundle',
  httpMethod: 'POST',
  responses: {
    200: {
      bodyMapper: {
        type: {
          name: 'Sequence',
          element: { type: { name: 'Composite', className: 'TicketProduct' } },
        },
      },
    },
    default: {
      bodyMapper: Mappers.ApiError,
    },
  },
  requestBody: Parameters.body2,
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: 'json',
  serializer,
};
const getFareByTicketIDOperationSpec: coreClient.OperationSpec = {
  path: '/fares/V1/fare/{ticketId}',
  httpMethod: 'GET',
  responses: {
    200: {
      bodyMapper: Mappers.TicketProduct,
    },
    default: {
      bodyMapper: Mappers.ApiError,
    },
  },
  urlParameters: [Parameters.$host, Parameters.ticketId],
  headerParameters: [Parameters.accept],
  serializer,
};
